import { Fragment } from 'react'

import { DeepCloneTask } from '@/Core/Tasks/Utils/DeepCloneTask'
import { Form, FormField } from '@/Core/UI/Form'

import type { IFieldConfig } from '@/Core/Contracts/UI/Form'
import type { ComponentProps, ComponentType } from 'react'

/** Що ядровий `ResourceDetail` передає шаблону форми. */
export interface FormTemplateProps {
    crudContext?: { resource?: { fields?: Record<string, IFieldConfig> } }
    formContext: unknown
}

/** Блок над полем: отримує `formContext`, щоб сховатися разом із полем. */
export type FormBlock = ComponentType<{ formContext: unknown }>

/**
 * Шаблон форми ресурсу = ядрова форма за замовчуванням + блоки над полями.
 *
 * Ядровий `ResourceDetail` без `form.template` малює всі поля ресурсу підряд
 * (`Core/UI/CrudResource/ResourceDetail.jsx`, «Дефолтний рендеринг»: поля в
 * порядку оголошення, без `id`, кожне — `FormField` з описом поля). Окремого
 * місця для пояснення там немає, тож підказку до поля (`SPEC-007` §12,
 * `SPEC-012` §13) інакше довелося б ставити над списком, далеко від поля.
 * Тут — той самий рендеринг, що в ядрі, і `blocks[поле]` перед цим полем.
 *
 * Блок, якому треба зникати разом зі своїм полем (поле з `depFn`), сам
 * читає `formContext` — тому блок є компонентом, а не готовим елементом.
 */
export const formTemplateWithBlocks = (blocks: Record<string, FormBlock>) => {
    const FormTemplateWithBlocks = ({ crudContext, formContext }: FormTemplateProps) => {
        const fields = DeepCloneTask(crudContext?.resource?.fields ?? {})

        return (
            <Form formContext={formContext}>
                {Object.keys(fields).map((name) => {
                    if (name === 'id') return null

                    const Block = blocks[name]

                    return (
                        <Fragment key={name}>
                            {Block ? <Block formContext={formContext} /> : null}
                            {/* Опис поля — пропсами, як у ядровому рендерингу; тип ядра вужчий за опис ресурсу */}
                            <FormField {...({ ...fields[name], name } as ComponentProps<typeof FormField>)} />
                        </Fragment>
                    )
                })}
            </Form>
        )
    }

    return FormTemplateWithBlocks
}
